/** Reports and workspace usage (board 23: O04 analytics, A08/A15 usage, A16 invoices). English source. */
export const reportsEn = {
  nav: 'Reports',
  title: 'Reports',
  description:
    'How conversations went: volume, handoffs, resolution and feedback, with the usage and invoices of your plan. Aggregates only, never message content.',
  export: 'Export CSV',
  exported: 'The CSV report was downloaded.',
  loading: 'Loading the report',
  tabs: {
    quality: 'Service quality',
    teams: 'Team activity',
    tickets: 'Tickets',
    ai: 'AI usage',
    billing: 'Billing',
  },
  withheld: 'Withheld',
  smallSample: 'Small sample',
  fewer: 'Withheld: fewer than {{count}}',
  rateOf: '{{rate}} of {{count}}',
  previousValue: 'Previous period: {{count}}',
  responses: 'Responses: {{count}}',
  basedOn: 'Conversations measured: {{count}}',
  outOfFive: '{{value}} / 5',
  privacy:
    'To protect privacy, a number that stands on fewer than {{count}} conversations or responses is withheld, and so is the next smallest where a total would reveal it.',
  rangeNote: '{{from}} to {{to}}, days in {{zone}}.',
  partial: 'Part of this range is before reports started recording, so it is not counted.',
  partialSince: 'Reports started recording on {{date}}; conversations before then are not counted.',
  filters: {
    range: 'Dates',
    ranges: {
      '7d': 'Last 7 days',
      '30d': 'Last 30 days',
      '90d': 'Last 90 days',
      custom: 'Custom range',
    },
    from: 'From',
    to: 'To',
    team: 'Team',
    allTeams: 'All teams',
    channel: 'Channel',
    allChannels: 'All channels',
  },
  tiles: {
    conversations: 'Conversations',
    handoffs: 'Human handoffs',
    resolved: 'Resolved',
    feedback: 'Customer feedback',
    aiResolved: 'Resolved by AI',
    firstResponse: 'Median first response',
    resolution: 'Median time to resolve',
  },
  chart: {
    title: 'Conversations per day',
    summary:
      'Line chart of conversations per day from {{from}} to {{to}}, with the previous period. The table below lists every day.',
    current: 'This period',
    previous: 'Previous period ({{from}} – {{to}})',
    currentShort: 'this period',
    previousShort: 'previous period',
    caption: 'Days with fewer than {{count}} conversations are withheld and show as gaps.',
    table: 'Show the numbers as a table',
    date: 'Day',
  },
  reasons: {
    title: 'Why conversations went to people',
    empty: 'No handoffs in this range.',
    reason: 'Reason',
    count: 'Conversations',
    names: {
      customer_request: 'The customer asked for a person',
      ai_unavailable: 'The AI model was unavailable',
      ai_blocked: 'The AI reply was held back',
      ai_limit: 'The AI limit or budget was reached',
      ai_stopped: 'AI replies were stopped',
      no_agent: 'No AI agent was published',
      assist_mode: 'The AI only suggests replies',
      channel_ai_off: 'The AI does not answer on this channel',
      staff_takeover: 'A person took over',
      staff_reply: 'A person replied',
      staff_assign: 'Assigned to a person',
      staff_release: 'Released to the queue',
      authority_assignment: 'Assigned in Chatwoot',
      authority_handoff: 'Handed over in Chatwoot',
      human_reply: 'A person replied in Chatwoot',
      return_to_ai: 'Returned to the AI',
      unknown: 'Not recorded',
    },
  },
  quality: {
    title: 'Chats that went wrong',
    description:
      'Customers who wrote and got no answer within an hour, messages left with the contact form while nobody could answer, and closed chats opened again.',
    missed: 'Missed chats',
    missedNote: '{{rate}} of {{count}} chats where the customer wrote',
    offlineMessages: 'Contact form messages',
    offlineMessagesNote: 'Left while nobody could answer',
    reopened: 'Reopen rate',
    reopenedNote: 'Of {{count}} closed chats, opened again',
  },
  tickets: {
    title: 'Tickets',
    created: 'Created',
    createdNote: 'Opened in this range',
    closed: 'Closed',
    closedNote: 'Opened in this range, and closed since',
    openNow: 'Open now',
    openNowNote: 'Open or waiting for the customer, whenever they were opened',
    closeMedian: 'Median time to close',
    byPriority: 'By priority',
    bySource: 'By source',
    priority: 'Priority',
    source: 'Source',
    count: 'Tickets',
    priorities: {
      urgent: 'Urgent',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
      none: 'No priority',
    },
    sources: {
      offline_form: 'Contact form',
      staff: 'Staff',
      automation: 'Automation',
      email: 'Email',
    },
    emptyList: 'No tickets in this range.',
    empty: {
      title: 'No tickets in this date range',
      description:
        'Tickets come from the contact form, email, your team and automations. Try a different date range.',
    },
  },
  commerce: {
    title: 'Orders and refunds',
    description: 'Orders placed in conversations and refunds paid back, per currency.',
    empty: 'No orders or refunds in this range.',
    currency: 'Currency',
    orders: 'Orders',
    revenue: 'Paid',
    refunds: 'Refunds',
    refunded: 'Refunded',
  },
  teams: {
    title: 'Queue performance',
    description: 'Teams by the conversations in their queue.',
    team: 'Team',
    conversations: 'Conversations',
    firstResponse: 'Median first response',
    handoffRate: 'Handoff rate',
    feedback: 'Customer feedback',
    unnamed: 'Unnamed team',
    none: 'No team',
  },
  channels: {
    title: 'Channels',
    channel: 'Channel',
    unknown: 'Unknown channel',
  },
  replyTargets: {
    title: 'Reply-time targets',
    description:
      'How often a person replied within the brand’s reply-time target. A target is counted when its clock ends; clocks that ended without a person’s reply are counted apart, not as missed.',
    met: 'Targets met',
    metNote: 'Of {{count}} replies from a person',
    missed: 'Targets missed',
    missedNote: 'Replies that came after the target',
    firstReply: 'Median time to first reply',
    firstReplyNote: 'Working time, snoozed time left out. Replies measured: {{count}}',
    stopped: 'Ended without a reply',
    stoppedNote: 'The AI took the chat back, or it was resolved',
    byPriority: 'By priority',
    byDay: 'By day',
    replies: 'Replies',
    metShare: 'Met',
    none: 'No reply-time clocks ended in this range.',
    recordingSince: 'Reply-time targets have been recorded since {{date}}.',
    notRecorded:
      'Nothing recorded yet. Results appear once a person replies in a conversation that has a reply-time target.',
  },
  offers: {
    title: 'Chats offered to one person at a time',
    description:
      'Offers made in this range: accepted, declined or left to run out, why people declined, and the hand-offs nobody took.',
    accepted: 'Accepted',
    acceptedNote: 'Of {{count}} offers answered or left to run out',
    declined: 'Declined',
    expired: 'Ran out',
    acceptTime: 'Median time to accept',
    acceptTimeNote: 'Offers accepted: {{count}}',
    leftInQueue: 'Left in the queue',
    leftInQueueNote: 'Hand-offs nobody accepted, of {{count}}',
    declineReasons: 'Why people declined',
    reason: 'Reason',
    declinedCount: 'Offers declined',
    noDeclines: 'No offers were declined in this range.',
    reasons: {
      at_capacity: 'Too many chats',
      not_my_area: 'Not their area',
      going_offline: 'Going offline',
      other: 'Something else',
    },
    byTeam: 'By team',
    offered: 'Offers',
    none: 'No chats were offered in this range.',
    windowLimited: 'Offers are kept for 90 days, so earlier days in this range are not counted.',
  },
  aiDrafts: {
    title: 'AI drafts for review',
    description:
      'Drafts the AI wrote for a person to check before sending, and what people did with them.',
    sent: 'Sent',
    sentNote: 'Approved as written or edited, of {{count}} drafts people decided on',
    edited: 'Edited before sending',
    rejected: 'Rejected',
    rejectReasons: 'Why drafts were rejected',
    setAsideReasons: 'Why drafts were set aside',
    reason: 'Reason',
    drafts: 'Drafts',
    noRejects: 'No drafts were rejected in this range.',
    noSetAside: 'No drafts were set aside in this range.',
    rejectNames: {
      inaccurate: 'It was wrong',
      incomplete: 'Something was missing',
      wrong_tone: 'The tone was wrong',
      not_needed: 'No reply was needed',
      other: 'Something else',
    },
    setAsideNames: {
      customer_wrote: 'The customer wrote again',
      owner_changed: 'The conversation changed hands',
      answered: 'Someone replied by hand',
      replaced: 'A newer draft replaced it',
      resolved: 'The conversation was resolved',
    },
    byAgent: 'By AI agent',
    agent: 'AI agent',
    unknownAgent: 'No agent recorded',
    none: 'No drafts were written in this range.',
    windowLimited: 'Drafts are kept for 90 days, so earlier days in this range are not counted.',
  },
  ai: {
    replies: 'AI replies',
    tokens: 'AI tokens',
    tokensSplit: 'Input {{input}} · output {{output}}',
    limitHandoffs: 'Handed to people at the limit',
    limitHandoffsNote:
      'Conversations the AI could not take because the plan or budget was used up.',
    cache: 'Answer cache hit rate',
    notMeasured: 'Not measured',
    notMeasuredNote:
      'The answer cache counts per brand and day; there were no lookups here, or this view is by team or channel.',
    cacheNote: 'Share of opening questions answered from the cache (per brand, UTC days).',
  },
  empty: {
    title: 'No activity in this date range',
    description: 'Try a different date range or remove the filters.',
    reset: 'Reset dates and filters',
  },
  billing: {
    loading: 'Loading billing',
    plan: 'Plan: {{plan}}, {{price}} a month.',
    noPlan: 'No plan is assigned to this workspace, so nothing is limited.',
    alerts: 'Alerts this period',
    alert: '{{limit}} reached {{threshold}}% on {{date}}.',
    usedOf: '{{used}} of {{max}}',
    usedUnlimited: '{{used}} used, no limit',
    meterLabel: '{{limit}} used',
    perPeriod: 'This billing month (UTC).',
    inUse: 'In use now.',
    limits: {
      conversations: 'Conversations',
      aiReplies: 'AI replies',
      aiTokens: 'AI tokens',
      knowledgeStorageBytes: 'Knowledge storage',
      seats: 'Seats',
      webhookEndpoints: 'Webhook endpoints',
    },
    states: {
      unlimited: 'No limit',
      ok: 'Within the limit',
      warning: 'Near the limit',
      reached: 'Limit reached',
    },
    pastDue: {
      title: 'An invoice is overdue',
      description: 'Please pay it, or contact Hamnivo if the payment was already made.',
    },
    aiPaused: {
      plan_replies:
        'The plan’s AI replies for this month are used up. New conversations go to people until the next month or a larger plan.',
      plan_tokens:
        'The plan’s AI tokens for this month are used up. New conversations go to people until the next month or a larger plan.',
      budget_tokens:
        'Your AI budget for this month is used up. New conversations go to people until you raise it or the month ends.',
    },
    usage: {
      title: 'Workspace usage',
      period: 'Billing month {{from}} to {{to}}.',
    },
    budget: {
      title: 'AI budget',
      description:
        'Set your own monthly ceiling on AI tokens, below the plan. At the limit the AI pauses and conversations go to people.',
      tokens: 'Monthly AI token budget',
      tokensHint: 'Leave empty for no budget beyond the plan.',
      alerts: 'Alert me at',
      threshold: '{{value}}%',
      save: 'Save budget',
      saved: 'Budget saved.',
      readOnly: 'Only people who manage billing can change the budget.',
    },
    invoices: {
      title: 'Invoices',
      empty: 'No invoices yet.',
      number: 'Invoice',
      period: 'Month',
      total: 'Total',
      status: 'Status',
      due: 'Due',
      view: 'View',
      viewLabel: 'View invoice {{number}}',
      statuses: {
        draft: 'Draft',
        issued: 'Issued',
        paid: 'Paid',
        void: 'Void',
      },
      overdue: 'Overdue',
      paymentFailed: 'Payment failed',
      paidOn: 'Paid {{date}}',
    },
    invoice: {
      title: 'Invoice {{number}}',
      loading: 'Loading the invoice',
      period: '{{plan}} plan, {{from}} to {{to}}.',
      item: 'Item',
      quantity: 'Quantity',
      amount: 'Amount',
      planLine: '{{plan}} plan',
      meteredLine: '{{limit}} beyond the {{included}} included',
      total: 'Total {{amount}}',
      taxNote: 'Taxes are not included.',
    },
  },
};
